import { defineStore } from 'pinia';

// Re-use or define a more specific model type
// For now, using the same basic one from the API handler
interface AppModel {
  id: string | number;
  name: string;
  // Add other properties that your models have
}

interface AppModelsState {
  models: AppModel[];
  isLoading: boolean;
  error: string | null;
  isInitialized: boolean;
}

export const useAppModelsStore = defineStore('appModels', {
  state: (): AppModelsState => ({
    models: [],
    isLoading: false,
    error: null,
    isInitialized: false,
  }),

  getters: {
    allModels: (state) => state.models,
    getModelById: (state) => (id: string | number) => {
      return state.models.find(model => model.id === id);
    },
    hasModels: (state) => state.models.length > 0,
    isReady: (state) => state.isInitialized && !state.isLoading,
  },

  actions: {
    // Action to be called by a Nuxt plugin during SSR and client-side hydration
    async fetchModels() {
      if (this.isInitialized && this.models.length > 0 && !this.error) {
        // console.log('[AppModelsStore] Models already initialized and present.');
        return;
      }
      
      // console.log('[AppModelsStore] Initializing models...');
      this.isLoading = true;
      this.error = null;

      try {
        // Use $fetch for direct API calls within Nuxt actions if preferred,
        // or use useFetch if you need its features (like key-based caching, though API has its own)
        const { data, error } = await useFetch<AppModel[]>('/api/app-models', {
          key: 'app-models-fetch', // Unique key for useFetch caching/deduplication
          // Add any headers or options if your /api/app-models endpoint needs them
        });

        if (error.value) {
          // console.error('[AppModelsStore] Error fetching models:', error.value);
          this.error = error.value.message || 'Failed to fetch models';
          this.$patch({ models: [], isInitialized: true }); // Initialize even on error to prevent re-fetch loops
        } else if (data.value) {
          // console.log('[AppModelsStore] Models fetched successfully.', data.value);
          this.$patch({
            models: data.value,
            isInitialized: true,
          });
        } else {
          // console.warn('[AppModelsStore] No data returned for models, but no explicit error.');
          this.$patch({ models: [], isInitialized: true }); // Still mark as initialized
        }
      } catch (err: any) {
        // console.error('[AppModelsStore] Critical error in fetchModels:', err);
        this.error = err.message || 'A critical error occurred while fetching models.';
        this.$patch({ models: [], isInitialized: true });
      } finally {
        this.isLoading = false;
      }
    },

    // Optional: A simpler client-side initializer if needed, or if state is already hydrated
    clientInitialize() {
      if (this.isInitialized) {
        // console.log('[AppModelsStore] Already initialized client-side.');
        return;
      }
      // If models were hydrated from SSR, isInitialized might already be true.
      // This is more of a fallback or explicit client-side trigger if not using the plugin approach for init.
      if (this.models.length > 0) {
         this.isInitialized = true;
      } else {
        // console.log('[AppModelsStore] No models hydrated, consider calling fetchModels.');
        // Optionally trigger fetchModels() here if you expect client to always load if not hydrated
        // this.fetchModels(); 
      }
    }
  },
}); 